/**
 * Modal for managing a specific user's API credentials.
 *
 * Shows both direct credentials and set-inherited entries with
 * clear source labels. Allows admin to set/update/delete direct
 * credentials and change the user's assigned credential set.
 *
 * Schema-driven: renders one row per registry service (via
 * useCredentialSchemas()) rather than a hardcoded list, so admin-only
 * services (google_maps, lalamove, stripe) are reachable here too (§4.5).
 */

import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '@/services/apiClient'
import {
  Button,
  Badge,
  Alert,
  AlertDescription,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Spinner,
} from '@/components/ui'
import { Check, X } from 'lucide-react'
import { useCredentialSchemas } from '@/hooks/useCredentialSchemas'
import type { CredentialServiceSchema } from '@/types/credentialSchemas'

interface UserCredential {
  id: string | null
  service: string
  name: string
  config: Record<string, string> | null
  has_key: boolean
  source: 'direct' | 'credential_set'
  credential_set_name: string | null
  is_admin_managed: boolean
  set_by_name: string | null
  created_at: string | null
  updated_at: string | null
}

interface CredentialSetSummary {
  id: string
  name: string
}

interface UserAssignment {
  credential_set_id: string
  credential_set_name: string
}

interface UserCredentialsModalProps {
  userId: string
  userName: string
  userEmail: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface ServiceFormState {
  key: string
  config: Record<string, string>
  saving: boolean
}

export function UserCredentialsModal({ userId, userName, userEmail, open, onOpenChange }: UserCredentialsModalProps) {
  const [credentials, setCredentials] = useState<UserCredential[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Assignment state
  const [assignment, setAssignment] = useState<UserAssignment | null>(null)
  const [allSets, setAllSets] = useState<CredentialSetSummary[]>([])
  const [changingSet, setChangingSet] = useState(false)

  // Per-service form state
  const [formData, setFormData] = useState<Record<string, ServiceFormState>>({})

  const {
    data: schemas = [],
    isLoading: schemasLoading,
    error: schemasError,
    refetch: refetchSchemas,
  } = useCredentialSchemas()

  const fetchData = useCallback(async () => {
    try {
      const [credsRes, setsRes, assignmentRes] = await Promise.all([
        apiClient.fetch(`/api/admin/users/${userId}/credentials`),
        apiClient.fetch('/api/admin/credential-sets'),
        apiClient.fetch(`/api/admin/users/${userId}/credential-set`),
      ])

      if (!credsRes.ok) throw new Error('Failed to fetch credentials')
      if (!setsRes.ok) throw new Error('Failed to fetch credential sets')
      if (!assignmentRes.ok) throw new Error('Failed to fetch credential set assignment')

      const creds: UserCredential[] = await credsRes.json()
      const sets: Array<{ id: string; name: string; assigned_user_count: number }> = await setsRes.json()
      const assignmentJson = await assignmentRes.json()

      setCredentials(creds)
      setAllSets(sets.map((s) => ({ id: s.id, name: s.name })))
      setAssignment(
        assignmentJson && assignmentJson.credential_set_id
          ? {
              credential_set_id: assignmentJson.credential_set_id,
              credential_set_name: assignmentJson.credential_set_name ?? '',
            }
          : null,
      )

      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  useEffect(() => {
    if (open) {
      setIsLoading(true)
      setFormData({})
      setAssignment(null)
      fetchData()
    }
  }, [open, fetchData])

  const getCredential = (serviceId: string) => credentials.find((c) => c.service === serviceId)

  const getFormState = (serviceId: string): ServiceFormState =>
    formData[serviceId] || { key: '', config: {}, saving: false }

  const updateFormState = (serviceId: string, updates: Partial<ServiceFormState>) => {
    setFormData((prev) => ({
      ...prev,
      [serviceId]: { ...getFormState(serviceId), ...updates },
    }))
  }

  const handleSaveKey = async (schema: CredentialServiceSchema) => {
    const form = getFormState(schema.service)
    if (!form.key.trim()) return

    updateFormState(schema.service, { saving: true })
    try {
      const cred = getCredential(schema.service)
      // Admin POST replaces config wholesale — merge stored values under the
      // typed ones so untouched keys survive.
      const merged: Record<string, string> = {
        ...((cred?.config as Record<string, string> | null) ?? {}),
      }
      for (const field of schema.fields) {
        if (field.storage !== 'config') continue
        const typed = form.config[field.key]
        if (typed !== undefined && typed !== '') merged[field.key] = typed
        else if (typed === '') delete merged[field.key]
      }
      const body: Record<string, unknown> = {
        service: schema.service,
        name: schema.label,
        api_key: form.key.trim(),
      }
      if (Object.keys(merged).length > 0) body.config = merged

      const res = await apiClient.fetch(`/api/admin/users/${userId}/credentials`, {
        method: 'POST',
        body: JSON.stringify(body),
      })
      if (!res.ok) {
        const data = await res.json().catch(() => ({ detail: 'Failed' }))
        throw new Error(data.detail || 'Failed to save key')
      }
      updateFormState(schema.service, { key: '', config: {}, saving: false })
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save key')
      updateFormState(schema.service, { saving: false })
    }
  }

  const handleDeleteKey = async (serviceId: string) => {
    updateFormState(serviceId, { saving: true })
    try {
      const res = await apiClient.fetch(`/api/admin/users/${userId}/credentials/${serviceId}`, {
        method: 'DELETE',
      })
      if (!res.ok) throw new Error('Failed to delete key')
      updateFormState(serviceId, { key: '', config: {}, saving: false })
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete key')
      updateFormState(serviceId, { saving: false })
    }
  }

  const handleAssignSet = async (setId: string) => {
    setChangingSet(true)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${setId}/assign`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: [userId] }),
      })
      if (!res.ok) throw new Error('Failed to assign set')
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign set')
    } finally {
      setChangingSet(false)
    }
  }

  const handleRemoveSet = async () => {
    if (!assignment) return
    setChangingSet(true)
    try {
      const res = await apiClient.fetch(`/api/admin/credential-sets/${assignment.credential_set_id}/unassign`, {
        method: 'POST',
        body: JSON.stringify({ user_ids: [userId] }),
      })
      if (!res.ok) throw new Error('Failed to remove set')
      setAssignment(null)
      await fetchData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove set')
    } finally {
      setChangingSet(false)
    }
  }

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader>
          <ModalTitle>
            <div>
              Manage API Keys for: {userName}
              <div className="text-sm font-normal text-muted-foreground">{userEmail}</div>
            </div>
          </ModalTitle>
        </ModalHeader>
        <ModalBody>
          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {schemasError ? (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription className="flex items-center justify-between gap-2">
                <span>
                  Couldn't load service catalog
                  {schemasError instanceof Error ? ` — ${schemasError.message}` : ''}
                </span>
                <Button variant="ghost" size="sm" onClick={() => refetchSchemas()}>
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          ) : null}

          {isLoading || schemasLoading ? (
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          ) : (
            <>
              {/* Credential set assignment */}
              <div className="mb-5 p-3 bg-muted rounded-md">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm font-medium text-foreground">Credential Set: </span>
                    {assignment ? (
                      <Badge variant="default">{assignment.credential_set_name}</Badge>
                    ) : (
                      <span className="text-sm text-muted-foreground italic">None assigned</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {assignment && (
                      <Button variant="ghost" size="sm" onClick={handleRemoveSet} disabled={changingSet}>
                        Remove
                      </Button>
                    )}
                    {allSets.length > 0 && (
                      <select
                        className="text-sm border border-border rounded-sm px-2 py-1 bg-background text-foreground"
                        value=""
                        onChange={(e) => {
                          if (e.target.value) handleAssignSet(e.target.value)
                        }}
                        disabled={changingSet}
                      >
                        <option value="">{assignment ? 'Change set...' : 'Assign set...'}</option>
                        {allSets
                          .filter((s) => s.id !== assignment?.credential_set_id)
                          .map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                      </select>
                    )}
                    {changingSet && <Spinner size="sm" />}
                  </div>
                </div>
              </div>

              {/* Per-service keys */}
              <div className="space-y-4">
                {schemas.map((schema) => {
                  const cred = getCredential(schema.service)
                  const form = getFormState(schema.service)
                  const isInherited = cred?.source === 'credential_set'
                  const isDirect = cred?.source === 'direct'
                  return (
                    <div key={schema.service} className="border-b border-border pb-4 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-foreground">{schema.label}</span>
                        {isInherited ? (
                          <Badge variant="default">Inherited: {cred.credential_set_name}</Badge>
                        ) : isDirect ? (
                          <Badge variant="success">
                            <Check className="w-3 h-3 mr-1" />
                            Direct {cred.is_admin_managed ? '(admin)' : '(user)'}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Not Configured</Badge>
                        )}
                      </div>

                      {isInherited ? (
                        <p className="text-sm text-muted-foreground italic">
                          Provided by credential set — set a direct override below to replace.
                        </p>
                      ) : null}

                      <div className="flex items-end gap-2 mt-1">
                        <div className="flex-1">
                          <Input
                            type="password"
                            placeholder={
                              isDirect
                                ? 'Enter new key to update...'
                                : isInherited
                                ? 'Enter key to set direct override...'
                                : 'Enter API key...'
                            }
                            value={form.key}
                            onChange={(e) => updateFormState(schema.service, { key: e.target.value })}
                          />
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleSaveKey(schema)}
                          disabled={!form.key.trim() || form.saving}
                          isLoading={form.saving}
                        >
                          {isDirect ? 'Update' : 'Save'}
                        </Button>
                        {isDirect && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteKey(schema.service)}
                                disabled={form.saving}
                                className="text-destructive hover:text-destructive/90"
                                aria-label="Delete direct credential"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Delete direct credential</TooltipContent>
                          </Tooltip>
                        )}
                      </div>

                      {schema.fields.filter((f) => f.storage === 'config').map((field) => {
                        const stored = (cred?.config as Record<string, string> | null)?.[field.key] ?? ''
                        const value = form.config[field.key] ?? stored
                        const onChange = (v: string) =>
                          updateFormState(schema.service, { config: { ...form.config, [field.key]: v } })
                        return (
                          <div className="mt-2" key={field.key}>
                            {field.enum_values.length > 0 && !field.dynamic_options ? (
                              <label className="block">
                                <span className="text-sm font-medium text-foreground">{field.label}</span>
                                <select
                                  className="mt-1 block w-full text-sm border border-border rounded-sm px-2 py-1.5 bg-background text-foreground"
                                  value={value}
                                  onChange={(e) => onChange(e.target.value)}
                                >
                                  <option value="">—</option>
                                  {field.enum_values.map((v) => (
                                    <option key={v} value={v}>{v}</option>
                                  ))}
                                </select>
                              </label>
                            ) : (
                              <Input
                                label={field.label}
                                placeholder={field.placeholder}
                                value={value}
                                onChange={(e) => onChange(e.target.value)}
                              />
                            )}
                            {field.help ? (
                              <p className="text-xs text-muted-foreground mt-1">{field.help}</p>
                            ) : null}
                          </div>
                        )
                      })}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button onClick={() => onOpenChange(false)}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
